import { useEffect, useState } from 'react'
import { getPhaserGameSync } from '../PhaserGame'
import type Game from '../scenes/Game'
import { phaserEvents, Event } from '../events/EventCenter'

/** While the game scene has no room yet. */
export const ROOM_POLL_INTERVAL_MS = 500
/** Safety net once a room is known, for room changes that emit no reconnect event. */
export const ROOM_FOLLOW_INTERVAL_MS = 2000

function findColyseusRoom(): any | null {
  try {
    const phaserGame = getPhaserGameSync()
    if (!phaserGame) return null
    const game = phaserGame.scene?.keys?.game as Game | undefined
    return game?.network?.room ?? null
  } catch {
    return null
  }
}

/**
 * The office's Colyseus room, for overlays that mount outside the Phaser tree
 * (the video controller and the presentation views). The game scene can still
 * be booting when an overlay mounts, so the lookup polls until the room exists.
 *
 * It keeps following `network.room` afterwards: the token-fallback reconnect
 * binds a brand-new Room while LiveKit (and so the controller) stays mounted,
 * and a hook holding the old Room would listen to a dead decoder and send into
 * a closed connection for the rest of the session.
 *
 * Typed `any` on purpose: callers read fields the shared `IPlayer` type does not
 * declare (`userId`, `chatMessages`), exactly as they did before this hook.
 */
export function useColyseusRoom(): any | null {
  const [room, setRoom] = useState<any | null>(() => findColyseusRoom())
  const hasRoom = room !== null

  useEffect(() => {
    const sync = () => {
      const current = findColyseusRoom()
      setRoom((prev: any | null) => (prev === current ? prev : current))
    }

    sync()
    // Emitted after Network.bindColyseusRoom on every reconnect path (SDK, token, rejoin fallback).
    phaserEvents.on(Event.COLYSEUS_RECONNECTED, sync)
    const interval = setInterval(sync, hasRoom ? ROOM_FOLLOW_INTERVAL_MS : ROOM_POLL_INTERVAL_MS)

    return () => {
      phaserEvents.off(Event.COLYSEUS_RECONNECTED, sync)
      clearInterval(interval)
    }
  }, [hasRoom])

  return room
}

export default useColyseusRoom
